import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FaCheckCircle, FaArrowRight, FaBookOpen } from 'react-icons/fa';
import { usePageContent } from '../services/pageContentService';
import HtmlContent from '../components/HtmlContent';
import RawHtmlPage, { isRawHtmlPage } from '../components/RawHtmlPage';

const AimsAndScope = () => {
  const location = useLocation();
  const { content, loading } = usePageContent('aims_and_scope');

  // Smooth scroll to anchor if hash is present in URL
  useEffect(() => {
    if (location.hash) {
      const hashId = location.hash.replace('#', '');
      const timer = setTimeout(() => {
        const el = document.getElementById(hashId);
        if (el) {
          const header = document.querySelector('header');
          const headerHeight = header ? header.offsetHeight : 140;
          const elementPosition = el.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - (headerHeight + 20);
          window.scrollTo({
            top: Math.max(0, offsetPosition),
            behavior: 'smooth'
          });
        }
      }, 120);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  const titles = content?.titles || {};

  const defaultScopeItems = [
    'Clinical research and clinical trials in homoeopathy',
    'Basic and applied biomedical investigations into ultra-dilutions',
    'Homoeopathic materia medica and pharmacological validations',
    'Organon of medicine and historical philosophical methodologies',
    'Evidence-based case reports and clinical case series (CARE guidelines)',
    'Public health, preventative healthcare, and community epidemiology',
    'Integrative and complementary clinical healthcare models',
    'Educational paradigms and pedagogical advances in homoeopathy',
    'Biostatistical methodology and evidence-based medicine standards',
  ];

  const scopeItems = (Array.isArray(content?.scope_items) && content.scope_items.length > 0)
    ? content.scope_items
    : defaultScopeItems;

  const defaultArticleTypes = [
    { title: 'Original Research Articles', desc: 'Controlled clinical trials, observational studies, and basic laboratory investigations (3,000–5,000 words).' },
    { title: 'Clinical Case Reports', desc: 'Rigorous case reports adhering strictly to CARE guidelines with long-term follow-up (1,500–3,000 words).' },
    { title: 'Systematic Reviews & Meta-Analyses', desc: 'Critical comprehensive syntheses of published clinical and preclinical literature (4,000–6,000 words).' },
    { title: 'Philosophical & Historical Insights', desc: 'Scholarly analyses of Organon principles and classical repertory methodologies (2,500–4,000 words).' },
    { title: 'Short Communications & Letters', desc: 'Preliminary findings or letters to the editor on published literature (1,000–1,800 words).' },
  ];

  const articleCategories = (Array.isArray(content?.article_types) && content.article_types.length > 0)
    ? content.article_types
    : defaultArticleTypes;

  // Wait for the saved text rather than flashing the built-in default first
  if (loading) return <div className="min-h-screen" />;

  if (isRawHtmlPage(content)) {
    return <RawHtmlPage html={content.full_content} />;
  }

  return (
    <div className="bg-[#FAF9F6] min-h-screen text-gray-800 font-sans pb-16">
      {/* Breadcrumb Bar */}
      <div className="bg-white py-3.5 px-4 sm:px-8 text-xs text-gray-500 shadow-xs">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/" className="text-[#03478E] hover:text-[#023366] hover:underline font-medium">
            Home
          </Link>
          <span>&gt;</span>
          <span className="font-semibold text-gray-700">Aims &amp; Scope</span>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <main className="bg-white rounded-3xl p-8 sm:p-12 shadow-sm space-y-10">
          <div>
              <span className="text-xs font-bold text-[#03478E] uppercase tracking-widest block mb-1">
                EDITORIAL SCOPE
              </span>
              <h1 className="page-title">
                {titles.page_title || content?.page_title || 'Aims & Scope'}
              </h1>
              {content?.overview && (
                <div className="mt-4 text-sm sm:text-base text-gray-600 leading-relaxed border-l-4 border-[#03478E] pl-4 bg-blue-50/40 py-2 rounded-r-xl">
                  <HtmlContent content={content.overview} />
                </div>
              )}
            </div>

            {/* ================= FULL PAGE TEXT (IF ENTERED AS SINGLE DOCUMENT) ================= */}
            {content?.full_content ? (
              <div className="prose max-w-none text-gray-800 leading-relaxed space-y-6 border-t border-gray-100 pt-6">
                <HtmlContent content={content.full_content} />
              </div>
            ) : (
              /* ================= COHESIVE SINGLE PAGE SECTIONS (ALL OPEN) ================= */
              <div className="space-y-10">
                
                {/* 3.1 Aim */}
                <section
                  id="aim"
                  className="scroll-mt-28 sm:scroll-mt-32 space-y-4 pt-2 border-t border-gray-100"
                >
                  <div className="pb-2 border-b border-gray-100">
                    <h2 className="section-heading">
                      {titles.aim || 'Aim'}
                    </h2>
                  </div>
                  <HtmlContent
                    content={content?.aim}
                    fallback="To provide an authoritative, peer-reviewed, open-access international platform dedicated to publishing high-quality, methodologically sound scientific research in homoeopathic medicine and allied integrative healthcare disciplines."
                    className="text-sm sm:text-base text-gray-700 leading-relaxed bg-[#FAFBFD] p-5 rounded-2xl border border-gray-100"
                  />
                </section>

                {/* 3.2 Scope */}
                <section
                  id="scope"
                  className="scroll-mt-28 sm:scroll-mt-32 space-y-4 pt-4 border-t border-gray-100"
                >
                  <div className="pb-2 border-b border-gray-100">
                    <h2 className="section-heading">
                      {titles.scope || 'Scope & Subject Areas'}
                    </h2>
                  </div>
                  <p className="text-sm text-gray-600">
                    AJHM welcomes original contributions across all areas of scientific and clinical homoeopathy, including:
                  </p>
                  <ul className="space-y-2.5 text-xs sm:text-sm text-gray-700">
                    {scopeItems.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2.5 bg-[#FAFBFD] p-3.5 rounded-xl border border-gray-100">
                        <FaCheckCircle className="text-[#03478E] text-sm mt-0.5 flex-shrink-0" />
                        <HtmlContent content={item} as="span" className="flex-1" />
                      </li>
                    ))}
                  </ul>
                </section>

                {/* 3.3 Geographical Scope */}
                <section
                  id="geographical-scope"
                  className="scroll-mt-28 sm:scroll-mt-32 space-y-4 pt-4 border-t border-gray-100"
                >
                  <div className="pb-2 border-b border-gray-100">
                    <h2 className="section-heading">
                      {titles.geographical_scope || 'Geographical Scope'}
                    </h2>
                  </div>
                  <HtmlContent
                    content={content?.geographical_scope}
                    fallback="Global in reach and international in authorship. While anchored in India—the global epicenter of homoeopathic healthcare—the journal invites contributions from all continents."
                    className="text-sm sm:text-base text-gray-700 leading-relaxed bg-[#FAFBFD] p-5 rounded-2xl border border-gray-100"
                  />
                </section>

                {/* 3.4 Target Audience */}
                <section
                  id="target-audience"
                  className="scroll-mt-28 sm:scroll-mt-32 space-y-4 pt-4 border-t border-gray-100"
                >
                  <div className="pb-2 border-b border-gray-100">
                    <h2 className="section-heading">
                      {titles.target_audience || 'Target Audience'}
                    </h2>
                  </div>
                  <HtmlContent
                    content={content?.target_audience}
                    fallback="A diverse international readership across healthcare, academic, and clinical environments including clinicians, postgraduate scholars, pharmacological researchers, and healthcare policy makers."
                    className="text-sm sm:text-base text-gray-700 leading-relaxed mb-3"
                  />
                  {Array.isArray(content?.target_audience_bullets) && content.target_audience_bullets.length > 0 && (
                    <ul className="space-y-2 text-xs sm:text-sm text-gray-700">
                      {content.target_audience_bullets.map((aud, aIdx) => (
                        <li key={aIdx} className="flex items-start gap-2.5 bg-[#FAFBFD] p-3 rounded-xl border border-gray-100">
                          <FaCheckCircle className="text-[#03478E] text-sm mt-0.5 flex-shrink-0" />
                          <HtmlContent content={aud} as="span" className="flex-1" />
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                {/* 3.5 Editorial Scope and Peer Review */}
                <section
                  id="editorial-scope"
                  className="scroll-mt-28 sm:scroll-mt-32 space-y-4 pt-4 border-t border-gray-100"
                >
                  <div className="pb-2 border-b border-gray-100">
                    <h2 className="section-heading">
                      {titles.editorial_scope || 'Editorial Scope and Peer Review'}
                    </h2>
                  </div>
                  <HtmlContent
                    content={content?.editorial_scope}
                    fallback="Every submitted manuscript undergoes rigorous double-blind peer review by qualified external subject experts. Submissions are judged strictly on clinical validity, methodological soundness, ethical adherence, and originality."
                    className="text-sm sm:text-base text-gray-700 leading-relaxed bg-[#FAFBFD] p-5 rounded-2xl border border-gray-100"
                  />
                </section>

                {/* 3.6 Article Types */}
                <section
                  id="article-types"
                  className="scroll-mt-28 sm:scroll-mt-32 space-y-4 pt-4 border-t border-gray-100"
                >
                  <div className="pb-2 border-b border-gray-100">
                    <h2 className="section-heading">
                      {titles.article_types || 'Article Types & Word Counts'}
                    </h2>
                  </div>
                  <div className="space-y-3 pt-2">
                    {articleCategories.map((art, idx) => (
                      <div key={idx} className="bg-[#FAFBFD] p-5 rounded-2xl border border-gray-100 space-y-1.5">
                        <h4 className="font-bold text-gray-900 text-sm sm:text-base">
                          {art.title}
                        </h4>
                        <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                          {art.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>

              </div>
            )}

            {/* Quick Actions (Single Page Flow) */}
            <div className="border-t border-gray-100 pt-8 flex items-center justify-end gap-3">
              <Link
                to="/upload"
                className="inline-flex items-center gap-2 py-2.5 px-5 bg-[#03478E] hover:bg-[#023366] text-white text-xs font-bold rounded-full uppercase tracking-wider transition-all shadow-xs"
              >
                <span>Submit Manuscript</span>
                <FaArrowRight className="text-[10px]" />
              </Link>
              <Link
                to="/editorial-board"
                className="inline-flex items-center gap-2 py-2.5 px-4 bg-white border border-gray-200 text-[#03478E] hover:bg-gray-50 text-xs font-bold rounded-full uppercase tracking-wider transition-all"
              >
                <FaBookOpen className="text-xs" />
                <span>Editorial Board</span>
              </Link>
            </div>

          </main>
        </div>
      </div>
    );
  };

export default AimsAndScope;
